import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { AddressSuggestion, MapboxAddressService } from 'src/app/Services/mapbox-address.service';
import { TranslocoModule } from '@jsverse/transloco';

/**
 * Reusable address typeahead backed by Mapbox (MapboxAddressService).
 *
 * Free-text tolerant (requirement A): every keystroke is emitted via valueChange, so the
 * user's typed value is ALWAYS preserved even if Mapbox returns nothing or errors —
 * selecting a suggestion is an optional enhancement that additionally emits structured
 * components via addressSelected. The parent maps those onto its own model fields
 * (e.g. Suburb vs SuburbTown), which is how this unifies artist/gallery addresses (E).
 *
 * Standalone so it drops into any NgModule via `imports`, without AppModule bloat.
 */
@Component({
  selector: 'app-address-autocomplete',
  standalone: true,
  imports: [FormsModule, TranslocoModule],
  templateUrl: './address-autocomplete.component.html',
  styleUrls: ['./address-autocomplete.component.css'],
})
export class AddressAutocompleteComponent implements OnDestroy {
  @Input() label = '';
  @Input() placeholder = 'Start typing an address, suburb, or city…';
  @Input() required = false;
  @Input() value = '';
  @Output() valueChange = new EventEmitter<string>();
  /** Emitted when the user picks a suggestion; carries the structured components. */
  @Output() addressSelected = new EventEmitter<AddressSuggestion>();

  suggestions: AddressSuggestion[] = [];
  loading = false;
  showSuggestions = false;

  private readonly query$ = new Subject<string>();
  private readonly sub: Subscription;

  constructor(private mapbox: MapboxAddressService) {
    this.sub = this.query$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((q) => {
          this.loading = true;
          return this.mapbox.search(q).pipe(catchError(() => of([] as AddressSuggestion[])));
        })
      )
      .subscribe((results) => {
        this.suggestions = results;
        this.loading = false;
        this.showSuggestions = results.length > 0;
      });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onInput(text: string): void {
    this.value = text;
    this.valueChange.emit(text); // free text always preserved
    this.query$.next(text);
  }

  select(s: AddressSuggestion): void {
    this.value = s.label;
    this.valueChange.emit(s.label);
    this.addressSelected.emit(s);
    this.suggestions = [];
    this.showSuggestions = false;
  }

  /** Delay hiding so a suggestion click registers before the input blurs. */
  hideSoon(): void {
    setTimeout(() => (this.showSuggestions = false), 200);
  }
}
